/**
 * @module return-payment/return-order
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import { ReturnPaymentMethods } from '@experience/shop/return-payment/interfaces/return-payment.interface';
import { AuthorizationAmount, Payment } from '@support/api/proxies/requests/api-2604.interface';
import { RETURN_PAYMENT_CONSTANTS } from '@experience/shop/return-payment/return-payment-constants/return-payment.constants';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ReturnPaymentService } from '../services/return-payment.service';

export class Api2604Model {
  public transactionType: string;
  public reason: string;
  public orderType: string;
  public originalOrderId: string;
  public paymentMethodCodeAction?: string;
  public userId?: string;
  public overrideIndicator?: boolean;
  public payment: Payment[];

  constructor(
    reason: string,
    orderType: string,
    originalOrderId?: string,
    refundPaymentMethod?: ReturnPaymentMethods[],
    objectService?: ObjectService,
    stateStoreUtilsService?: StateStoreUtilsService,
    returnPaymentService?: ReturnPaymentService
  ) {
    this.transactionType = RETURN_PAYMENT_CONSTANTS.REFUND;
    this.reason = reason;
    this.orderType = orderType;
    this.originalOrderId = originalOrderId;
    if (returnPaymentService.isDefaultPaymentMethodOverrided(refundPaymentMethod)) {
      this.paymentMethodCodeAction = RETURN_PAYMENT_CONSTANTS.UPDATE;
      const cashReturnPayment = refundPaymentMethod.find(
        (refundPaymentMethods: ReturnPaymentMethods) =>
          refundPaymentMethods.selectedRefundMethod === RETURN_PAYMENT_CONSTANTS.CASH
      );
      const cashReturnAmount = parseFloat(cashReturnPayment.refundAmount);
      if (cashReturnAmount > RETURN_PAYMENT_CONSTANTS.MAX_AMOUNT) {
        this.overrideIndicator = true;
        this.userId = stateStoreUtilsService.getManagerUserID()
          ? stateStoreUtilsService.getManagerUserID()
          : stateStoreUtilsService.getApplicationUserId();
      }
    }
    this.payment = this.getPaymentMethods(refundPaymentMethod, objectService, returnPaymentService);
  }

  /**
   * used to get payment method
   * @param {ReturnPaymentMethods[]} refundPaymentMethods
   * @param {ObjectService} objectService
   * @return {Payment[]}
   */
  private getPaymentMethods(
    refundPaymentMethods?: ReturnPaymentMethods[],
    objectService?: ObjectService,
    returnPaymentService?: ReturnPaymentService
  ): Payment[] {
    const refundPayment = [];
    const isPaymentMethodOverrided =
      returnPaymentService.isDefaultPaymentMethodOverrided(refundPaymentMethods);
    refundPaymentMethods.forEach((refundPaymentMethod: ReturnPaymentMethods) => {
      refundPayment.push(
        this.getRefundPaymentMethod(
          refundPaymentMethod,
          objectService,
          refundPaymentMethods[0],
          isPaymentMethodOverrided
        )
      );
    });
    return refundPayment;
  }

  /**
   * used to build individual payment method
   * @param {ReturnPaymentMethods} refundPaymentMethod
   * @param {ObjectService} objectService
   * @return {Payment}
   */
  private getRefundPaymentMethod(
    refundPaymentMethod: ReturnPaymentMethods,
    objectService: ObjectService,
    firstRefund: ReturnPaymentMethods,
    isPaymentMethodOverrided?: boolean
  ): Payment {
    const payment: Payment = {
      newRefund: {},
    };
    payment.newRefund.paymentMethodCode = refundPaymentMethod.selectedRefundMethod;
    payment.newRefund.receivedPaymentId = refundPaymentMethod.receivedPaymentId;
    payment.authorizationAmount = this.getAuthorizationAmount(
      objectService.get(refundPaymentMethod, 'refundAmount', '0')
    );
    if (refundPaymentMethod.selectedRefundMethod === RETURN_PAYMENT_CONSTANTS.PREPAID) {
      payment.payeeParty = {
        personName: {
          firstName:
            objectService.get(refundPaymentMethod, 'address.firstName') ||
            objectService.get(firstRefund, 'address.firstName'),
          middleName:
            objectService.get(refundPaymentMethod, 'address.middleName') ||
            objectService.get(firstRefund, 'address.middleName'),
          familyName:
            objectService.get(refundPaymentMethod, 'address.lastName') ||
            objectService.get(firstRefund, 'address.lastName'),
        },
      };
      payment.addressCommunication = {
        id:
          objectService.get(refundPaymentMethod, 'address.addressId') ||
          objectService.get(firstRefund, 'address.addressId'),
        usageContext: ['MAILING'],
      };
    }
    if (isPaymentMethodOverrided) {
      payment.originalPaymentMethod = refundPaymentMethod.originalRefundMethod;
    }
    return payment;
  }

  /**
   * used to get authorization amount
   * @param {string} amount
   * @return {AuthorizationAmount}
   */
  private getAuthorizationAmount(amount: string): AuthorizationAmount {
    return {
      currencyCode: 'USD',
      text: amount.replace(/\`|\$|,|\s/g, '') || '0',
    };
  }
}
